import React, { useCallback } from 'react';
import { graphql, useFragment } from 'react-relay';
import Button from '@atlaskit/button';
import EmptyState from '@atlaskit/empty-state';
import {
	CORE_PROJECT,
	type ProjectType as ProjectTypeKey,
} from '@atlassian/jira-common-constants/src/project-types.tsx';
import { useIntl } from '@atlassian/jira-intl';
import { useAnalyticsEvents, fireUIAnalytics } from '@atlassian/jira-product-analytics-bridge';
import { ProjectCreateDrawerConsumer } from '@atlassian/jira-project-create-drawer-context/src/index.tsx';
import { FilterFields } from '@atlassian/jira-projects-directory-v3-utils/src/constants.tsx';
import type { emptyView_projectsDirectoryV3$key } from '@atlassian/jira-relay/src/__generated__/emptyView_projectsDirectoryV3.graphql';
import { useFilter } from '../../../controllers/filter-state/index.tsx';
import businessImageUrl from './assets/briefcase.svg';
import emptyImageUrl from './assets/empty.svg';
import messages from './messages.tsx';
import { getProjectTypeInfo } from './utils.tsx';

type OpenDrawerMethod = (
	attributes:
		| {
				isNextGen?: boolean;
				showExperienceSelection?: boolean;
				recommendedTemplate?: {
					templateKey: string;
					isSimplified: boolean;
				};
				defaultSelectedTemplateKey?: string;
				isTemplatePickerOpenInitially?: boolean;
				selectedProjectType?: ProjectTypeKey | null;
		  }
		| undefined,
) => void;

export interface Props {
	dataRef: emptyView_projectsDirectoryV3$key;
}

export const EmptyView = ({ dataRef }: Props) => {
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const analyticsEvent = createAnalyticsEvent({
		action: 'clicked',
		actionSubject: 'emptyView',
	});

	const [filter] = useFilter();
	const projectType = filter[FilterFields.ProjectType];

	const { jira } = useFragment<emptyView_projectsDirectoryV3$key>(
		graphql`
			fragment emptyView_projectsDirectoryV3 on Query {
				jira {
					canCreateProject: canPerform(type: CREATE_PROJECT, cloudId: $cloudId)
						@optIn(to: "JiraAction")
				}
			}
		`,
		dataRef,
	);

	const projectTypeInfo = getProjectTypeInfo(projectType);
	const { recommendedTemplate, defaultSelectedTemplateKey } = projectTypeInfo;

	const handleCreate = useCallback(
		(open: OpenDrawerMethod) => {
			open({
				isNextGen: false,
				showExperienceSelection: false,
				recommendedTemplate,
				isTemplatePickerOpenInitially: true,
				// eslint-disable-next-line @typescript-eslint/consistent-type-assertions
				selectedProjectType: projectType as ProjectTypeKey,
				defaultSelectedTemplateKey,
			});
			fireUIAnalytics(analyticsEvent, 'CreateProjectButton');
		},
		[analyticsEvent, defaultSelectedTemplateKey, projectType, recommendedTemplate],
	);

	if (!jira?.canCreateProject || !projectTypeInfo?.projectType) {
		return projectType === CORE_PROJECT ? (
			<EmptyState
				imageUrl={businessImageUrl}
				header={formatMessage(messages.jiraCoreHeading)}
				description={formatMessage(messages.businessEmptyStateContactAdminDescription)}
			/>
		) : (
			<EmptyState
				imageUrl={emptyImageUrl}
				header={formatMessage(messages.defaultEmptyMessage)}
				description={formatMessage(messages.defaultEmptyMessageDescription)}
				headingLevel={2}
			/>
		);
	}

	const { imageUrl, heading, description } = projectTypeInfo;

	return (
		<ProjectCreateDrawerConsumer>
			{({ methods: { open } }) => (
				<EmptyState
					imageUrl={imageUrl}
					header={formatMessage(heading)}
					description={formatMessage(description)}
					primaryAction={
						<Button appearance="primary" onClick={() => handleCreate(open)}>
							{formatMessage(messages.createProjectPrompt)}
						</Button>
					}
				/>
			)}
		</ProjectCreateDrawerConsumer>
	);
};
